import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import apiClient from '@/core/api/client';
import { API_ENDPOINTS } from '@/core/api/endpoints';
import { theme } from '@/theme';
import { toast } from '@/shared/components';

const contactFormSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters'),
  email: z.string().email('Please enter a valid email address'),
  phone: z.string().optional().or(z.literal('')),
  subject: z.string().min(3, 'Subject must be at least 3 characters'),
  message: z.string().min(10, 'Message must be at least 10 characters')
});

type ContactFormInput = z.infer<typeof contactFormSchema>;

export const ContactPage: React.FC = () => {
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<ContactFormInput>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: '',
      email: '',
      phone: '',
      subject: '',
      message: ''
    }
  });

  const onSubmit = async (data: ContactFormInput) => {
    setLoading(true);
    try {
      await apiClient.post(API_ENDPOINTS.PUBLIC.CONTACT, data);
      toast.success('Your message has been submitted successfully! We will get back to you shortly.');
      reset();
    } catch (err: any) {
      console.error(err);
      toast.error(err.response?.data?.error?.message || err.response?.data?.message || 'Failed to submit message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="responsive-contact-wrapper page-enter" style={{ background: theme.colors.bg, minHeight: '100vh', padding: '120px 32px 80px' }}>
      {/* Responsive Styles for Contact Page */}
      <style>{`
        @media (max-width: 991px) {
          .responsive-contact-wrapper {
            padding: 60px 16px 40px !important;
          }
          .responsive-contact-wrapper h1 {
            font-size: 2.2rem !important;
            line-height: 1.25 !important;
          }
          .responsive-contact-grid {
            grid-template-columns: 1fr !important;
            gap: 30px !important;
          }
          .responsive-contact-form {
            padding: 24px !important;
          }
        }
      `}</style>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '60px' }}>
          <h1 style={{
            fontSize: '3rem',
            fontWeight: 800,
            color: theme.colors.text.heading,
            letterSpacing: '-0.5px',
            marginBottom: '20px'
          }}>
            Contact Our Global Team
          </h1>
          <p style={{
            fontSize: '1.1rem',
            color: theme.colors.text.muted,
            maxWidth: '650px',
            margin: '0 auto',
            lineHeight: 1.6
          }}>
            Have questions about university options, application management, or subscription tiers? Get in touch with our counseling staff.
          </p>
        </div>

        {/* Layout Grid */}
        <div className="responsive-contact-grid" style={{ display: 'grid', gridTemplateColumns: '0.8fr 1.2fr', gap: '50px' }}>
          
          {/* Left Panel: Contact info */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
            <div className="card" style={{ padding: '32px' }}>
              <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--clr-text-heading)', marginBottom: '24px' }}>
                Corporate Office
              </h2>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <MapPin size={20} color="var(--clr-primary)" style={{ marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--clr-text-heading)', fontSize: '0.95rem' }}>Location</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--clr-text-muted)', marginTop: '2px' }}>
                      100 Queens Park, Suite 400<br/>Toronto, ON M5S 2C6, Canada
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <Mail size={20} color="var(--clr-primary)" style={{ marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--clr-text-heading)', fontSize: '0.95rem' }}>Email Inquiries</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--clr-text-muted)', marginTop: '2px' }}>
                      support@eduglobal.io<br/>admissions@eduglobal.io
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
                  <Phone size={20} color="var(--clr-primary)" style={{ marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <div style={{ fontWeight: 600, color: 'var(--clr-text-heading)', fontSize: '0.95rem' }}>Phone Numbers</div>
                    <div style={{ fontSize: '0.875rem', color: 'var(--clr-text-muted)', marginTop: '2px' }}>
                      +1 (800) 555-0199 (Toll Free)<br/>+1 (416) 555-0142 (International)
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="card" style={{ padding: '32px', background: 'rgba(115, 103, 240, 0.03)', border: '1px dashed var(--clr-primary-light)' }}>
              <h3 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--clr-primary-dark)', marginBottom: '8px' }}>
                Need Immediate Help?
              </h3>
              <p style={{ fontSize: '0.85rem', color: 'var(--clr-text-muted)', lineHeight: 1.6 }}>
                Try our AI-powered course matching engine or read our FAQ knowledge base to resolve study destination queries instantly.
              </p>
            </div>
          </div>

          {/* Right Panel: Form */}
          <div className="card responsive-contact-form" style={{ padding: '40px' }}>
            <h2 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--clr-text-heading)', marginBottom: '24px' }}>
              Send Us A Message
            </h2>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label" htmlFor="name">Full Name *</label>
                  <input
                    id="name"
                    type="text"
                    placeholder="e.g. Jane Doe"
                    className={`form-input ${errors.name ? 'form-input--error' : ''}`}
                    {...register('name')}
                  />
                  {errors.name && <span className="form-error">{errors.name.message}</span>}
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="email">Email Address *</label>
                  <input
                    id="email"
                    type="email"
                    placeholder="e.g. jane@company.com"
                    className={`form-input ${errors.email ? 'form-input--error' : ''}`}
                    {...register('email')}
                  />
                  {errors.email && <span className="form-error">{errors.email.message}</span>}
                </div>
              </div>

              <div className="grid-2">
                <div className="form-group">
                  <label className="form-label" htmlFor="phone">Phone Number (Optional)</label>
                  <input
                    id="phone"
                    type="text"
                    placeholder="e.g. +1 (555) 019-9922"
                    className="form-input"
                    {...register('phone')}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label" htmlFor="subject">Subject *</label>
                  <input
                    id="subject"
                    type="text"
                    placeholder="e.g. Admissions Inquiry"
                    className={`form-input ${errors.subject ? 'form-input--error' : ''}`}
                    {...register('subject')}
                  />
                  {errors.subject && <span className="form-error">{errors.subject.message}</span>}
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '30px' }}>
                <label className="form-label" htmlFor="message">Message *</label>
                <textarea
                  id="message"
                  rows={5}
                  placeholder="Outline your detailed inquiry here..."
                  className={`form-textarea ${errors.message ? 'form-input--error' : ''}`}
                  {...register('message')}
                />
                {errors.message && <span className="form-error">{errors.message.message}</span>}
              </div>

              <button type="submit" disabled={loading} className="btn btn-primary" style={{ width: '100%', height: '44px' }}>
                {loading ? 'Sending Message...' : 'Send Message'} <Send size={14} style={{ marginLeft: '6px' }} />
              </button>
            </form>
          </div>

        </div>

      </div>
    </div>
  );
};
